import { USER_API_URL } from '@repo/editor/src/api/constants';
import { ActionOrigin } from '@repo/editor/src/api/generators';
import { getAccessToken } from '@repo/editor/src/api/user';
import { PoWChallenge } from '@repo/editor/src/types';
import { fetchRetry, tryFetchRetry } from '@repo/editor/src/utils/fetchRetry';

import {
  GeneratedContent,
  ImageGenPayload,
  ScenesGroup,
  VectorizeInfo,
  VectorizingSolver,
} from 'global/types/Generators';

const defaultBackend = 'AD_INPAINT';
const defaultSortOption = 'recent';

// eslint-disable-next-line @typescript-eslint/ban-types
type GenericResponse<T = {}> =
  | { success?: false; error: string }
  | ({ success: true } & T);

export type GeneratedContentResponse = GenericResponse<{
  results: GeneratedContent[];
  total: number;
}>;

export interface ApiErrorResponse {
  success: false;
  error: string;
  errorMessage?: string;
}

type RemoveBackgroundResponse =
  | { success: true; id: string; objectName: string }
  | ApiErrorResponse;

export enum GeneratedContentStatus {
  Started = 'STARTED',
  Complete = 'COMPLETE',
  Failed = 'FAILED',
}

export type ReplaceBackgroundResponse =
  | {
      success: true;
      id: string;
      status: GeneratedContentStatus;
      objectName?: string;
    }
  | ApiErrorResponse;

export type VectorizeResponse = GenericResponse<{
  id: string;
  status: GeneratedContentStatus;
}>;

export type ShowVectorizedImageResponse = GenericResponse<VectorizeInfo>;

export type ImageGenErrorResponse = {
  success: false;
  error: string;
  errorMessage?: string;
  issues?: Record<string, unknown>[]; // an issue object created by zod
  errorObject?: Error; // an error if thrown on the client
  id?: string;
};

export type ImageGenSuccessResponse = {
  success: true;
  result: string;
};

export type ImageGenResponse = ImageGenSuccessResponse | ImageGenErrorResponse;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createAPI = ({ url }: { url: string }) => {
  const getPromptScenesGroups = async (): Promise<
    GenericResponse<{ results: ScenesGroup[] }>
  > => {
    return await tryFetchRetry<GenericResponse<{ results: ScenesGroup[] }>>(
      `${url}/promptscenes/groups/index`
    );
  };

  const selectGeneratedContent = async (
    take: number,
    skip?: number,
    exclude?: GeneratedContentStatus
  ): Promise<GeneratedContentResponse> => {
    const response = await fetchRetry(
      `${url}/generators/generated_content?backend=${defaultBackend}&sort=${defaultSortOption}&take=${take}${
        skip ? `&skip=${skip}` : ''
      }${exclude ? `&excludeStatus=${exclude}` : ''}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );

    return await response.json();
  };

  const removeBackground = async (
    imageUrl: string,
    actionOrigin?: ActionOrigin
  ): Promise<RemoveBackgroundResponse> => {
    return await tryFetchRetry<RemoveBackgroundResponse>(
      `${url}/generators/remove_background`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({
          image: imageUrl,
          actionOrigin,
        }),
      }
    );
  };

  const replaceBackground = async (
    imageUrl: string,
    promptId?: string,
    prompt?: string,
    negativePrompt?: string
  ): Promise<ReplaceBackgroundResponse> => {
    return await tryFetchRetry<ReplaceBackgroundResponse>(
      `${url}/generators/replace_background`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({
          image: imageUrl,
          promptId: promptId,
          prompt: prompt,
          negativePrompt: negativePrompt,
        }),
      }
    );
  };

  const checkGeneratedImage = async (
    id: string
  ): Promise<ReplaceBackgroundResponse> => {
    return await tryFetchRetry<ReplaceBackgroundResponse>(
      `${url}/generators/${id}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );
  };

  const vectorize = async (
    imgURL: string,
    solver: VectorizingSolver,
    colorCount: number,
    cropBox?: number[]
  ): Promise<VectorizeResponse> => {
    return await tryFetchRetry<VectorizeResponse>(
      `${url}/generators/vectorize`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({
          url: imgURL,
          solver,
          colorCount,
          cropBox,
          // Deprecated:
          // This will be unnecessary after the backend is updated to only support delayed requests
          delayed: true,
        }),
      }
    );
  };

  const showVectorizedImage = async (
    id: string
  ): Promise<ShowVectorizedImageResponse> => {
    return await tryFetchRetry<ShowVectorizedImageResponse>(
      `${url}/generators/vectorize/${id}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );
  };

  const generatePublicImage = async (
    payload: ImageGenPayload,
    powChallenge?: PoWChallenge
  ): Promise<ImageGenResponse> => {
    return await tryFetchRetry<ImageGenResponse>(
      `${url}/generators/public/image`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-challenge-nonce': powChallenge?.nonce ?? '',
          'x-challenge-solution': powChallenge?.solution?.toString() ?? '',
        },
        body: JSON.stringify(payload),
        retries: 0,
      }
    );
  };

  const vectorizePublic = async (
    payload: {
      objectName: string;
      colorCount: number;
      cropBox?: number[];
    },
    powChallenge?: PoWChallenge
  ) => {
    return await tryFetchRetry<GenericResponse<{ objectName?: string }>>(
      `${url}/generators/public/vectorize`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-challenge-nonce': powChallenge?.nonce ?? '',
          'x-challenge-solution': powChallenge?.solution?.toString() ?? '',
        },
        body: JSON.stringify(payload),
        retries: 0,
      }
    );
  };

  return {
    getPromptScenesGroups,
    selectGeneratedContent,
    removeBackground,
    replaceBackground,
    checkGeneratedImage,
    vectorize,
    vectorizePublic,
    showVectorizedImage,
    generatePublicImage,
  };
};

export default createAPI({ url: USER_API_URL });
